import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Clock, ChevronRight, ShieldCheck, GraduationCap, Users } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog';
import type { DirectoryAppraiser } from '@/hooks/useDirectoryAppraisers';
import { getDesignations } from '@/hooks/useDirectoryAppraisers';
import { SupervisorInquiryForm } from './SupervisorInquiryForm';

interface SupervisorCardProps {
  appraiser: DirectoryAppraiser;
}

export function SupervisorCard({ appraiser }: SupervisorCardProps) {
  const [dialogOpen, setDialogOpen] = useState(false);

  const initials = (appraiser.name || 'A')
    .split(' ')
    .map(n => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const designations = getDesignations(appraiser.certifications);
  const profilePath = `/directory/${appraiser.slug || appraiser.id}`;
  const location = [appraiser.city, appraiser.state].filter(Boolean).join(', ');

  const currentYear = new Date().getFullYear();
  const years = appraiser.appraiser_since
    ? currentYear - appraiser.appraiser_since
    : appraiser.years_experience;

  return (
    <Card className="group overflow-hidden hover:shadow-lg transition-all duration-200 border-border/60">
      <CardContent className="p-6">
        {/* Accepting Trainees badge */}
        <div className="flex items-center gap-2 mb-4">
          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-200 dark:border-emerald-800 gap-1">
            <GraduationCap className="h-3 w-3" />
            Accepting Trainees
          </Badge>
          {appraiser.max_trainees != null && appraiser.max_trainees > 0 && (
            <Badge variant="outline" className="text-xs gap-1">
              <Users className="h-3 w-3" />
              Up to {appraiser.max_trainees} trainee{appraiser.max_trainees !== 1 ? 's' : ''}
            </Badge>
          )}
        </div>

        <div className="flex items-start gap-4">
          <Avatar className="h-14 w-14 flex-shrink-0 ring-2 ring-border/50">
            {appraiser.headshot_url ? (
              <AvatarImage src={appraiser.headshot_url} alt={appraiser.name || 'Appraiser'} />
            ) : null}
            <AvatarFallback className="text-base font-semibold bg-primary/10 text-primary">
              {initials}
            </AvatarFallback>
          </Avatar>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-lg text-foreground leading-tight">
                {appraiser.name || 'Appraiser'}
              </h3>
              {appraiser.is_verified && <ShieldCheck className="h-4 w-4 text-primary flex-shrink-0" />}
              {designations.map(d => (
                <Badge key={d} className="bg-primary text-primary-foreground text-[10px] px-1.5 py-0 font-bold">
                  {d}
                </Badge>
              ))}
            </div>
            {appraiser.title && (
              <p className="text-sm text-muted-foreground mt-0.5 truncate">{appraiser.title}</p>
            )}
          </div>
        </div>

        {/* Supervisor license types */}
        {appraiser.supervisor_license_types && appraiser.supervisor_license_types.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {appraiser.supervisor_license_types.map(lt => (
              <Badge key={lt} variant="outline" className="text-xs">
                Can supervise: {lt}
              </Badge>
            ))}
          </div>
        )}

        {/* Meta */}
        <div className="mt-3 space-y-1.5">
          {location && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
              <span className="truncate">{location}</span>
            </div>
          )}
          {years != null && years > 0 && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="h-3.5 w-3.5 flex-shrink-0" />
              <span>{years}+ years experience</span>
            </div>
          )}
        </div>

        {/* Supervisor bio */}
        {appraiser.supervisor_bio && (
          <p className="mt-3 text-sm text-muted-foreground line-clamp-3 italic">
            "{appraiser.supervisor_bio}"
          </p>
        )}

        {/* Actions */}
        <div className="mt-4 flex items-center gap-2">
          <Button asChild variant="outline" size="sm" className="flex-1">
            <Link to={profilePath}>
              View Profile <ChevronRight className="h-3.5 w-3.5 ml-1" />
            </Link>
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="flex-1 gap-1.5">
                <Users className="h-3.5 w-3.5" />
                Contact Supervisor
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Contact {appraiser.name}</DialogTitle>
                <DialogDescription>
                  Send a trainee inquiry to {appraiser.name}. Your information will only be shared with this appraiser.
                </DialogDescription>
              </DialogHeader>
              <SupervisorInquiryForm
                appraiserUserId={appraiser.user_id}
                appraiserName={appraiser.name || 'this appraiser'}
                supervisorLicenseTypes={appraiser.supervisor_license_types}
              />
            </DialogContent>
          </Dialog>
        </div>
      </CardContent>
    </Card>
  );
}
